import React, { useState, useEffect } from "react";
import { Navigate, Link } from 'react-router-dom'
import Cookies from 'universal-cookie';
import '../index.css';
import logo from '../assets/spinner.gif'
import { populateInventoryList, UseLocalStorage, GetAttributesByAssignment } from "../functions";

function Inventory() {
  const [localStorageAttributes, setLocalStorageAttributes] = UseLocalStorage('attributes', []);
  const [localStorageInventory, setLocalStorageInventory] = UseLocalStorage('inventory', []);
  const inventoryAttributes = GetAttributesByAssignment('inventory', localStorageAttributes);
  const inventoryTags = inventoryAttributes?.tags?.sort((a, b) => a.displayorder.localeCompare(b.displayorder)) || [];

  const [pageTitle, setPageTitle] = UseLocalStorage('inventoryPageTitle', 'Inventory');
  const [pageAttr, setPageAttr] = UseLocalStorage('inventoryPageAttr', 'Item');

  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);

  const cookies = new Cookies();
  let login = cookies.get('login');

  useEffect(() => {
    if (login) {
      const loadPost = async () => {
        setLoading(true);
        let inventory = await populateInventoryList();
        inventory.sort((a, b) => a.item.localeCompare(b.item)); // sort by item name
        setLocalStorageInventory(inventory);
        setFilteredResults(inventory);

        let attributes = localStorageAttributes;
        const inventoryAttributes = GetAttributesByAssignment('inventory', attributes);
        if (inventoryAttributes) {
          setPageTitle(inventoryAttributes.displayplural);
          setPageAttr(inventoryAttributes.displayname);
        }

        setLoading(false);
      };
      loadPost();
    }
  }, [])

  if (!login) {
    return (<Navigate to={{ pathname: '../Login' }} />);
  }

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching

    // Filter logic - search across multiple fields
    const results = localStorageInventory.filter(
      (item) =>
        item.item.toLowerCase().includes(query) ||
        (item.description && item.description.toLowerCase().includes(query)) ||
        (item.location && item.location.toLowerCase().includes(query))
    );
    setFilteredResults(results);
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResults = filteredResults.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    if (currentPage > 1) {
      pageNumbers.push(
        <button key="prev" onClick={() => handlePageChange(currentPage - 1)}>
          Previous
        </button>
      );
    }

    // First page
    if (startPage > 1) {
      pageNumbers.push(
        <button key={1} onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="ellipsis1">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          style={{
            backgroundColor: currentPage === i ? '#444444' : '#333333',
            fontWeight: currentPage === i ? 'bold' : 'normal'
          }}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2">...</span>);
      }
      pageNumbers.push(
        <button key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </button>
      );
    }

    // Next button
    if (currentPage < totalPages) {
      pageNumbers.push(
        <button key="next" onClick={() => handlePageChange(currentPage + 1)}>
          Next
        </button>
      );
    }

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredResults.length)} of {filteredResults.length} results
        </div>
        <div className="pagination-buttons">
          {pageNumbers}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="Inventory">
        <h1>{pageTitle}</h1>
        <div className="home-stats">
          <div className="stat-item">
            <span className="stat-number">{filteredResults.length}</span>
            <span className="stat-label">Total {pageTitle}</span>
          </div>
        </div>
      </div>
      <div className="search-and-actions">
        <div className="left-actions">
          <Link to="/InventoryMaint" className="new-ticket-btn">
            <span className="btn-icon">+</span>
            New {pageAttr}
          </Link>
        </div>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by item, description, or location"
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
        </div>
        <div className="right-actions">
          {/* Space for future actions like export/print */}
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <img src={logo} style={{ width: '50px' }} alt="loading..." />
          <div className="loading-text">Loading inventory...</div>
        </div>
      ) : (
        <>
          {currentResults.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <h3>No inventory found</h3>
              <p>
                {searchQuery 
                  ? `No inventory items match your search for "${searchQuery}"`
                  : "No inventory items available. Create your first inventory item to get started!"
                }
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <div id="inventoryList" className="centered-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Description</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentResults.map((item) => (
                        <tr key={item.item}>
                          <td>
                            <Link to="/InventoryMaint" state={item}>
                              <strong>{item.item}</strong>
                            </Link>
                          </td>
                          <td>
                            <Link to="/InventoryMaint" state={item}>
                              {item.description || '-'}
                            </Link>
                          </td>
                          <td>
                            <Link to="/InventoryMaint" state={item}>
                              {item.qty || '0'}
                            </Link>
                          </td>
                          <td>
                            <Link to="/InventoryMaint" state={item}>
                              {item.price ? `$${item.price}` : '-'}
                            </Link>
                          </td>
                          <td>
                            <Link to="/InventoryMaint" state={item}>
                              {item.location || '-'}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-view">
                <div className="inventory-cards">
                  {currentResults.map((item) => (
                    <div key={item.item} className="inventory-card">
                      <Link to="/InventoryMaint" state={item} className="card-link">
                        <div className="card-header">
                          <span className="inventory-name">{item.item}</span>
                          <span className="inventory-icon">📦</span>
                        </div>
                        <div className="card-body">
                          {item.description && (
                            <div className="card-field">
                              <span className="field-label">Description:</span>
                              <span className="field-value">{item.description}</span>
                            </div>
                          )}
                          <div className="card-field">
                            <span className="field-label">Quantity:</span>
                            <span className="field-value">{item.qty || '0'}</span>
                          </div>
                          {item.price && (
                            <div className="card-field">
                              <span className="field-label">Price:</span>
                              <span className="field-value">${item.price}</span>
                            </div>
                          )}
                          {item.location && (
                            <div className="card-field">
                              <span className="field-label">Location:</span>
                              <span className="field-value">{item.location}</span>
                            </div>
                          )}
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {!loading && renderPagination()}
    </>
  );
}

export default Inventory;